import { useState, useMemo } from "react";
import {
  Search,
  ArrowDownLeft,
  ArrowUpRight,
  Wallet,
  TrendingUp,
  RefreshCw,
  Download,
  ChevronLeft,
  ChevronRight,
  Check,
  X,
  AlertCircle,
  Plus,
  ArrowLeft,
} from "lucide-react";
import { useAdminWallets } from "../../hooks/useWallet";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { toast } from "sonner";
import { makeRequest } from "@/utils/makeRequest";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { getErrorMessage } from "@/utils/getErrorMessage";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { AdminVerificationDialog } from "@/components/admin-components/AdminVerificationDialog";
import type { Wallet as SystemWalletInterface } from "@/interfaces/wallet";
import { useNavigate } from "react-router-dom";
import Loader from "@/components/loader/Loader";
import { queryClient } from "@/services/queryClient";
import { ErrorState } from "@/components/ErrorState";
import { useWalletAnalytics } from "@/hooks";
import { formatCurrency } from "@/utils/formatCurrency";

const systemTypes: string[] = ["TREASURY", "FEE", "HOT", "COLD"];

const AdminWallets = () => {
  const [assetSearchQuery, setAssetSearchQuery] = useState("");
  const [assetCurrentPage, setAssetCurrentPage] = useState(1);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [systemType, setSystemType] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [openVerification, setOpenVerification] = useState(false);

  const navigate = useNavigate();

  const itemsPerPage = 10;
  const assetsPerPage = 5;

  const { refetch } = useAdminWallets();
  const { data: walletAnalytics } = useWalletAnalytics();

  console.log("WALLET ANALYTICS ", walletAnalytics);

  const fetchSystemWallets = async () => {
    const res = await makeRequest.get("/wallet/system");

    return res.data;
  };

  const {
    data: wallets,
    isLoading,
    isError,
    refetch: refetchSystemWallets,
  } = useQuery<SystemWalletInterface[]>({
    queryKey: ["wallets:system"],
    queryFn: fetchSystemWallets,
  });

  console.log("WALLETS", wallets);

  console.log(wallets);

  // Mock data for demonstration
  const walletStats = [
    {
      label: "Total Assets",
      value: "$45.2M",
      change: "+5.2%",
      icon: Wallet,
      positive: true,
    },
    {
      label: "24h Deposits",
      value: "$2.1M",
      change: "+12%",
      icon: ArrowDownLeft,
      positive: true,
    },
    {
      label: "24h Withdrawals",
      value: "$1.8M",
      change: "-3%",
      icon: ArrowUpRight,
      positive: false,
    },
    {
      label: "Active Wallets",
      value: "8,432",
      change: "+8%",
      icon: TrendingUp,
      positive: true,
    },
  ];

  const assetDistribution = [
    {
      name: "BTC",
      value: 35,
      color: "#F0B90B",
      amount: 245.5,
      usdValue: 15700000,
      change24h: "+2.3%",
    },
    {
      name: "ETH",
      value: 28,
      color: "#627EEA",
      amount: 1842,
      usdValue: 6300000,
      change24h: "-1.2%",
    },
    {
      name: "USDT",
      value: 22,
      color: "#26A17B",
      amount: 12400000,
      usdValue: 12400000,
      change24h: "+0.01%",
    },
    {
      name: "NGNT",
      value: 10,
      color: "#008751",
      amount: 2100000,
      usdValue: 2100000,
      change24h: "0.00%",
    },
    {
      name: "SOL",
      value: 3,
      color: "#14F195",
      amount: 15000,
      usdValue: 1800000,
      change24h: "+5.7%",
    },
    {
      name: "BNB",
      value: 2,
      color: "#F3BA2F",
      amount: 2500,
      usdValue: 750000,
      change24h: "+1.1%",
    },
  ];

  // Filter assets based on search
  const filteredAssets = useMemo(() => {
    if (!assetSearchQuery.trim()) return assetDistribution;
    return assetDistribution.filter((asset) =>
      asset.name.toLowerCase().includes(assetSearchQuery.toLowerCase()),
    );
  }, [assetSearchQuery]);

  // Asset pagination
  const assetTotalPages = Math.ceil(filteredAssets.length / assetsPerPage);
  const paginatedAssets = filteredAssets.slice(
    (assetCurrentPage - 1) * assetsPerPage,
    assetCurrentPage * assetsPerPage,
  );

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "completed":
        return (
          <span className="flex items-center gap-1 px-2 py-1 rounded text-xs bg-emerald-500/10 text-emerald-500">
            <Check className="w-3 h-3" /> Completed
          </span>
        );
      case "pending":
        return (
          <span className="flex items-center gap-1 px-2 py-1 rounded text-xs bg-primary/10 text-primary">
            <AlertCircle className="w-3 h-3" /> Pending
          </span>
        );
      case "failed":
        return (
          <span className="flex items-center gap-1 px-2 py-1 rounded text-xs bg-red-500/10 text-red-500">
            <X className="w-3 h-3" /> Failed
          </span>
        );
      default:
        return <span className="text-muted-foreground">{status}</span>;
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await refetch();
    await refetchSystemWallets();
    setTimeout(() => {
      setIsRefreshing(false);
      // toast({
      //   title: "Refreshed",
      //   description: "Wallet data has been updated.",
      // });
    }, 500);
  };

  const handleSendMessage = (e: React.FormEvent) => {
    e.preventDefault();

    if (!systemType || !name) {
      toast.warning("Please fill all required fields.");
      return;
    }

    // open verification instead of calling API
    setOpenVerification(true);
  };

  const handleVerify = async ({
    otp,
    securityCode,
  }: {
    otp: string;
    securityCode: string;
  }) => {
    try {
      const payload = {
        otp,
        securityCode,
        systemType,
        name,
        chain: "polygon",
      };

      const res = await makeRequest.post("/wallet/system/wallet", payload);

      if (res.data) {
        queryClient.invalidateQueries({ queryKey: ["wallets:system"] });

        toast.success("System wallet successfully created.");

        setOpenVerification(false);
        setShowMessageModal(false);

        setSystemType("");
        setName("");
      }
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  if (isLoading) return <Loader type="bounce" />;

  if (isError)
    return (
      <ErrorState
        onRetry={() => {
          refetch();
          refetchSystemWallets();
        }}
      />
    );

  return (
    <div className="min-h-screen bg-background px-2 sm:px-4 lg:px-6">
      <div className="max-w-7xl mx-auto">
        <Button
          className="hidden lg;flex"
          variant="ghost"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        {/* Header */}
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold">
              Wallet Management
            </h1>
            <p className="text-muted-foreground mt-1">
              Monitor platform assets and transactions
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Button
              className="w-fit"
              variant="outline"
              size="sm"
              onClick={() => setShowMessageModal(true)}
            >
              <Plus className="mr-2 h-4 w-4" />
              Create Wallet
            </Button>

            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={handleRefresh}
                disabled={isRefreshing}
                className="p-2 bg-card rounded-lg text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
              >
                <RefreshCw
                  className={`w-5 h-5 ${isRefreshing ? "animate-spin" : ""}`}
                />
              </button>
              <button
                // onClick={handleExport}
                className="flex items-center gap-2 px-4 py-2 bg-card text-foreground rounded-lg hover:bg-muted transition-colors"
              >
                <Download className="w-4 h-4" />
                Export
              </button>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-8">
          {walletStats.map((stat, i) => (
            <div key={i} className="nex-card p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <stat.icon className="w-5 h-5 text-primary" />
                </div>
                <span
                  className={`text-xs ${stat.positive ? "text-emerald-500" : "text-red-500"}`}
                >
                  {stat.change}
                </span>
              </div>
              <p className="text-2xl font-bold text-foreground">{stat.value}</p>
              <p className="text-xs text-muted-foreground">{stat.label}</p>
            </div>
          ))}
        </div>

        <div className="flex flex-col gap-4">
          <h2>System Wallets</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-8">
            {wallets?.map((wallet) => {
              if (!wallet.systemType) return null;

              const systemType = wallet.systemType;

              const totalBalance = Number(wallet.totalBalance);

              return (
                <div
                  key={systemType}
                  onClick={() =>
                    navigate(`/admin/wallets/${systemType.toLowerCase()}`)
                  }
                  className="nex-card p-4 cursor-pointer hover:scale-[1.02] transition-all"
                >
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm text-muted-foreground">
                      {wallet.systemType}
                    </p>
                    <Wallet className="w-5 h-5 text-primary" />
                  </div>

                  <p className="text-xl font-bold text-foreground">
                    ${formatCurrency(totalBalance)}
                    {/* ${totalBalance.toLocaleString()} */}
                  </p>

                  <p className="text-xs text-muted-foreground">
                    {wallet.assets.length} assets
                    {wallet.assets.length > 1 ? "s" : ""}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Asset Distribution & Holdings */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mb-8">
          <div className="nex-card p-6">
            <h3 className="font-semibold text-foreground text-lg mb-2">
              Asset Distribution
            </h3>
            <p className="text-sm text-muted-foreground mb-4">By total value</p>
            <div className="h-56 sm:h-64 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={assetDistribution}
                    cx="50%"
                    cy="50%"
                    innerRadius={45}
                    outerRadius={65}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {assetDistribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "hsl(var(--card))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "8px",
                    }}
                    itemStyle={{ color: "hsl(var(--foreground))" }}
                    formatter={(value: number) => [`${value}%`, "Share"]}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex flex-wrap gap-3 mt-4">
              {assetDistribution.map((item) => (
                <div key={item.name} className="flex items-center gap-2">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: item.color }}
                  />
                  <span className="text-xs text-muted-foreground">
                    {item.name}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-2 nex-card p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
              <div>
                <h3 className="font-semibold text-foreground text-lg">
                  Asset Holdings
                </h3>
                <p className="text-sm text-muted-foreground">
                  Platform-wide asset balances
                </p>
              </div>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search assets..."
                  value={assetSearchQuery}
                  onChange={(e) => {
                    setAssetSearchQuery(e.target.value);
                    setAssetCurrentPage(1);
                  }}
                  className="pl-10 pr-4 py-2 bg-card border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary"
                />
              </div>
            </div>

            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full min-w-[600px] sm:min-w-0">
                <thead className="bg-card">
                  <tr>
                    <th className="text-left text-xs font-medium text-muted-foreground py-3 px-4 whitespace-nowrap">
                      Asset
                    </th>
                    <th className="text-right text-xs font-medium text-muted-foreground py-3 px-4 whitespace-nowrap">
                      Balance
                    </th>
                    <th className="text-right text-xs font-medium text-muted-foreground py-3 px-4 whitespace-nowrap">
                      Value (USD)
                    </th>
                    <th className="text-right text-xs font-medium text-muted-foreground py-3 px-4 whitespace-nowrap">
                      24h Change
                    </th>
                    <th className="text-right text-xs font-medium text-muted-foreground py-3 px-4 whitespace-nowrap">
                      Share
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedAssets.map((asset) => (
                    <tr
                      key={asset.name}
                      className="border-b border-border hover:bg-muted/50 dark:hover:bg-[#252A32] transition-colors"
                    >
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-3">
                          <div
                            className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold"
                            style={{
                              backgroundColor: `${asset.color}20`,
                              color: asset.color,
                            }}
                          >
                            {asset.name[0]}
                          </div>
                          <div>
                            <p className="text-foreground font-medium">
                              {asset.name}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="text-right py-4 px-4 text-foreground font-mono">
                        {asset.amount.toLocaleString()}
                      </td>
                      <td className="text-right py-4 px-4 text-foreground font-mono">
                        ${asset.usdValue.toLocaleString()}
                      </td>
                      <td
                        className={`text-right py-4 px-4 font-mono ${asset.change24h.startsWith("+") ? "text-emerald-500" : asset.change24h.startsWith("-") ? "text-red-500" : "text-muted-foreground"}`}
                      >
                        {asset.change24h}
                      </td>
                      <td className="text-right py-4 px-4">
                        <span className="text-muted-foreground">
                          {asset.value}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="md:hidden space-y-3">
              {paginatedAssets.map((asset) => (
                <div key={asset.name} className="rounded-xl border bg-card p-4">
                  <div className="flex justify-between items-center mb-3">
                    <div className="flex items-center gap-3">
                      <div
                        className="w-10 h-10 rounded-full flex items-center justify-center font-bold"
                        style={{
                          backgroundColor: `${asset.color}20`,
                          color: asset.color,
                        }}
                      >
                        {asset.name[0]}
                      </div>

                      <div>
                        <p className="font-semibold">{asset.name}</p>

                        <p className="text-xs text-muted-foreground">
                          {asset.value}% of portfolio
                        </p>
                      </div>
                    </div>

                    <span
                      className={
                        asset.change24h.startsWith("+")
                          ? "text-emerald-500"
                          : asset.change24h.startsWith("-")
                            ? "text-red-500"
                            : "text-muted-foreground"
                      }
                    >
                      {asset.change24h}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-muted-foreground text-xs">Balance</p>

                      <p>{asset.amount.toLocaleString()}</p>
                    </div>

                    <div>
                      <p className="text-muted-foreground text-xs">USD Value</p>

                      <p>${asset.usdValue.toLocaleString()}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Asset Pagination */}
            {assetTotalPages > 1 && (
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-4 border-t border-border mt-4">
                <p className="text-sm text-muted-foreground">
                  Showing {(assetCurrentPage - 1) * assetsPerPage + 1} to{" "}
                  {Math.min(
                    assetCurrentPage * assetsPerPage,
                    filteredAssets.length,
                  )}{" "}
                  of {filteredAssets.length} assets
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() =>
                      setAssetCurrentPage((p) => Math.max(1, p - 1))
                    }
                    disabled={assetCurrentPage === 1}
                    className="p-2 rounded-lg bg-card text-muted-foreground disabled:opacity-50 hover:text-foreground transition-colors"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  {Array.from({ length: assetTotalPages }, (_, i) => i + 1).map(
                    (page) => (
                      <button
                        key={page}
                        onClick={() => setAssetCurrentPage(page)}
                        className={`px-3 py-1 rounded-lg text-sm ${
                          assetCurrentPage === page
                            ? "bg-primary text-primary-foreground"
                            : "bg-card text-muted-foreground hover:text-foreground"
                        }`}
                      >
                        {page}
                      </button>
                    ),
                  )}
                  <button
                    onClick={() =>
                      setAssetCurrentPage((p) =>
                        Math.min(assetTotalPages, p + 1),
                      )
                    }
                    disabled={assetCurrentPage === assetTotalPages}
                    className="p-2 rounded-lg bg-card text-muted-foreground disabled:opacity-50 hover:text-foreground transition-colors"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {showMessageModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-card rounded-xl p-5 sm:p-6 w-[95%] max-w-md">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-foreground">
                Create System Wallet
              </h3>
              <button
                onClick={() => setShowMessageModal(false)}
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="mb-4">
              <p className="text-sm text-muted-foreground">
                Select wallet type to create
              </p>
            </div>
            <form onSubmit={handleSendMessage} className="flex flex-col gap-5">
              <div>
                <Label className="block text-sm text-muted-foreground mb-1">
                  Wallet Name
                </Label>
                <Input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g Fee wallet"
                  className="pl-10 pr-10 h-11"
                />
              </div>
              <div>
                <label className="block text-sm text-muted-foreground mb-1">
                  Wallet Type
                </label>
                <select
                  value={systemType}
                  onChange={(e) => setSystemType(e.target.value)}
                  className="w-full px-3 py-2 bg-card border border-border rounded-lg text-foreground focus:outline-none focus:border-primary"
                >
                  <option value="">Select wallet type</option>
                  {systemTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>

              <div className="mt-4 flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowMessageModal(false)}
                  className="flex-1 py-2 border border-border text-foreground rounded-lg font-medium hover:bg-muted transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 disabled:cursor-not-allowed py-2 bg-primary text-primary-foreground rounded-lg font-medium hover:brightness-110 transition-all"
                >
                  {loading ? "Creating..." : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <AdminVerificationDialog
        open={openVerification}
        onOpenChange={setOpenVerification}
        onSuccess={handleVerify}
      />
    </div>
  );
};

export default AdminWallets;
